import { useState } from 'react';
import { useApi } from '../hooks.ts';
import { cx } from './ui.tsx';

export interface NorrbottenStats {
  municipalities: { name: string; participants: number; certified: number }[];
  other: { participants: number; certified: number };
  total: { participants: number; certified: number };
}

/**
 * Schematisk karta: en sexhörning per kommun, ungefär där kommunen ligger i länet (inte skalenlig).
 * Färgen visar antal intyg (en färgton, ljus till mörk) och siffran står alltid i rutan.
 */
const LAYOUT: Record<string, [col: number, row: number]> = {
  Kiruna: [1, 0],
  Gällivare: [1, 1],
  Pajala: [2, 1],
  Arjeplog: [0, 2],
  Jokkmokk: [1, 2],
  Överkalix: [2, 2],
  Övertorneå: [3, 2],
  Arvidsjaur: [0, 3],
  Älvsbyn: [1, 3],
  Boden: [2, 3],
  Kalix: [3, 3],
  Haparanda: [4, 3],
  Piteå: [1, 4],
  Luleå: [2, 4],
};

// Sekventiell skala i Auroras gröna ton, från ljus (få) till mörk (många).
const STEPS = ['#eef7f3', '#c6eadb', '#8fd3b8', '#3fb68b', '#23735b', '#174d3d'];
const DARK_FROM = 4; // vit text från och med detta steg

const R = 46;
const W = Math.sqrt(3) * R;
const PAD = 6;

function hexPath(cx: number, cy: number, r: number) {
  return (
    Array.from({ length: 6 }, (_, i) => {
      const a = (Math.PI / 180) * (60 * i - 30);
      return `${i ? 'L' : 'M'}${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`;
    }).join('') + 'Z'
  );
}

const nf = new Intl.NumberFormat('sv-SE');

export function stepFor(value: number, max: number): number {
  if (value <= 0 || max <= 0) return 0;
  return Math.min(STEPS.length - 1, Math.max(1, Math.ceil((value / max) * (STEPS.length - 1))));
}

export function NorrbottenMap({ highlight, compact }: { highlight?: string; compact?: boolean }) {
  const { data } = useApi<NorrbottenStats>('/api/stats/norrbotten');
  const [hover, setHover] = useState<string | null>(null);
  if (!data) return <div className="h-64" aria-hidden="true" />;

  const max = Math.max(...data.municipalities.map((m) => m.certified));
  const width = W * 5.5 + PAD * 2;
  const height = R * 1.5 * 4 + R * 2 + PAD * 2;
  const focus = data.municipalities.find((m) => m.name === (hover ?? highlight)) ?? null;

  return (
    <div className={cx('grid gap-6', !compact && 'md:grid-cols-[minmax(0,1fr)_16rem] md:items-center')}>
      <figure className="m-0">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="mx-auto block w-full max-w-md"
          role="img"
          aria-label={`Karta över Norrbottens kommuner. ${nf.format(data.total.certified)} lärare har tagit intyget.`}
        >
          {data.municipalities.map((m) => {
            const [col, row] = LAYOUT[m.name];
            const x = PAD + W / 2 + col * W + (row % 2 ? W / 2 : 0);
            const y = PAD + R + row * R * 1.5;
            const step = stepFor(m.certified, max);
            const ink = step >= DARK_FROM ? '#ffffff' : '#14254a';
            const active = m.name === hover || m.name === highlight;
            return (
              <g
                key={m.name}
                tabIndex={0}
                onMouseEnter={() => setHover(m.name)}
                onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(m.name)}
                onBlur={() => setHover(null)}
                className="cursor-default outline-none"
              >
                <title>{`${m.name}: ${m.certified} intyg, ${m.participants} deltagare`}</title>
                <path
                  d={hexPath(x, y, R - 2)}
                  fill={STEPS[step]}
                  stroke={active ? '#14254a' : '#ffffff'}
                  strokeWidth={active ? 3 : 2}
                />
                <text x={x} y={y - 6} textAnchor="middle" fontSize="11" fontWeight="600" fill={ink}>
                  {m.name}
                </text>
                <text
                  x={x}
                  y={y + 15}
                  textAnchor="middle"
                  fontSize="17"
                  fontWeight="800"
                  fill={ink}
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {nf.format(m.certified)}
                </text>
              </g>
            );
          })}
        </svg>
        <figcaption className="mt-2 flex flex-wrap items-center justify-center gap-2 text-xs text-muted">
          <span>Antal intyg:</span>
          <span>0</span>
          <span className="flex overflow-hidden rounded" aria-hidden="true">
            {STEPS.map((c) => (
              <span key={c} className="h-3 w-6" style={{ background: c }} />
            ))}
          </span>
          <span>{nf.format(max)}</span>
          <span>· Schematisk karta, inte skalenlig</span>
        </figcaption>
      </figure>
      {!compact && (
        <div className="space-y-4">
          <div>
            <p className="text-4xl font-extrabold tabular-nums text-night-900">
              {nf.format(data.total.certified)}
            </p>
            <p className="text-muted">lärare har tagit intyget</p>
          </div>
          <div>
            <p className="text-2xl font-bold tabular-nums text-night-900">
              {nf.format(data.total.participants)}
            </p>
            <p className="text-muted">lärare är med i kursen</p>
          </div>
          <p className="min-h-[3rem] rounded-xl bg-night-50 p-3 text-sm" aria-live="polite">
            {focus ? (
              <>
                <strong>{focus.name}:</strong> {nf.format(focus.certified)} intyg av{' '}
                {nf.format(focus.participants)} deltagare
              </>
            ) : (
              'Peka på en kommun för att se siffrorna.'
            )}
          </p>
        </div>
      )}
      {compact && (
        <p className="min-h-[1.5rem] text-center text-sm" aria-live="polite">
          {focus && (
            <>
              <strong>{focus.name}:</strong> {nf.format(focus.certified)} intyg av{' '}
              {nf.format(focus.participants)} deltagare
            </>
          )}
        </p>
      )}
      <details className={cx('text-sm', !compact && 'md:col-span-2')}>
        <summary className="cursor-pointer font-semibold text-night-700">Visa som tabell</summary>
        <table className="mt-3 w-full max-w-lg text-left">
          <thead>
            <tr className="border-b border-night-100 text-muted">
              <th className="py-1 font-semibold">Kommun</th>
              <th className="py-1 text-right font-semibold">Intyg</th>
              <th className="py-1 text-right font-semibold">Deltagare</th>
            </tr>
          </thead>
          <tbody>
            {[...data.municipalities]
              .sort((a, b) => b.certified - a.certified || a.name.localeCompare(b.name, 'sv'))
              .map((m) => (
                <tr key={m.name} className="border-b border-night-100/60">
                  <td className="py-1">{m.name}</td>
                  <td className="py-1 text-right tabular-nums">{nf.format(m.certified)}</td>
                  <td className="py-1 text-right tabular-nums">{nf.format(m.participants)}</td>
                </tr>
              ))}
            {data.other.participants > 0 && (
              <tr>
                <td className="py-1 text-muted">Annan kommun</td>
                <td className="py-1 text-right tabular-nums">{nf.format(data.other.certified)}</td>
                <td className="py-1 text-right tabular-nums">{nf.format(data.other.participants)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </details>
    </div>
  );
}
